import { PDFDocument } from "pdf-lib";
import { BaseExcelGenerator } from "../../../utils/excel/BaseExcelGenerator";
import { BasePdfGenerator } from "../../../utils/pdf/BasePdfGenerator";
import generateSingleYA from "./generateSingleYA";
import { generateIrasMainCompPdf } from "./iras";
import { generateIrasCrossYAMainCompPdf } from "./iras/cross_ya";
import { generateInvestCoMainCompPdf } from "./investco";
import { DEFAULT_UNABSORBED_SUMMARY, omitCrossYARowFields } from "./shared";
import {
  apportionAmount,
  getYAConfigValue,
} from "../CrossYACalculationSummary";
import {
  getCrossYAApportionmentContext,
  getCrossYABasisPeriod,
} from "../utils/precedingCalculation";

const EMPTY_UNABSORBED_SUMMARY = {
  caRows: [],
  lossRows: [],
  donationRows: [],
};

const normaliseAmount = (value) => parseFloat(value) || 0;

const hasManualUnutilisedRows = (...rowGroups) =>
  rowGroups.some((rows) =>
    (rows || []).some(
      (row) =>
        !row?.isForfeited &&
        (Math.abs(normaliseAmount(row?.preceding_amount)) > 0 ||
          Math.abs(normaliseAmount(row?.current_amount)) > 0 ||
          Math.abs(normaliseAmount(row?.amount)) > 0),
    ),
  );

const buildSingleYACompany = (company, amountKey) => {
  const isPrecedingYA = amountKey === "preceding_amount";
  const basisPeriod = getCrossYABasisPeriod(company, true, amountKey);

  return {
    ...company,
    taxPeriodType: "single_ya",
    yearOfAssessment:
      basisPeriod?.yearOfAssessment ??
      Number(company?.yearOfAssessment) - (isPrecedingYA ? 1 : 0),
    financialYearStart:
      basisPeriod?.financialYearStart ?? company?.financialYearStart,
    financialYearEnd:
      basisPeriod?.financialYearEnd ?? company?.financialYearEnd,
  };
};

const buildSingleYARow = (row, amountKey) => {
  const amount = Object.prototype.hasOwnProperty.call(row || {}, amountKey)
    ? normaliseAmount(row?.[amountKey])
    : normaliseAmount(row?.amount);

  return {
    ...omitCrossYARowFields(row),
    amount,
    historyAmount: [amount],
  };
};

const buildSingleYARows = (rows = [], amountKey) =>
  (rows || [])
    .map((row) => buildSingleYARow(row, amountKey))
    .filter(
      (row) =>
        row?.isForfeited ||
        row?.systemData ||
        row?.status === "autoload" ||
        Math.abs(normaliseAmount(row?.amount)) > 0,
    );

const getFinancialYear = (row) => {
  if (row?.date) {
    const year = new Date(row.date).getFullYear();
    if (!Number.isNaN(year)) return year;
  }

  if (typeof row?.yearOfAssessment === "number") {
    return row.yearOfAssessment;
  }

  const match = String(row?.yearOfAssessment ?? "").match(/(\d{4})/);
  return match ? Number(match[1]) : null;
};

const isGeneratedCrossYACurrentYearLossRow = (row) =>
  row?.row_id === "auto-preceding-ya-current-year-loss" ||
  row?.name === "Current Year Losses - Preceding YA" ||
  row?.reference === "Current Year Losses - Preceding YA" ||
  row?.reference === "[Auto Generated] Current Year Losses";

const getCurrentYALossesBroughtForward = (computedSummary) => {
  const currentYABroughtForward = normaliseAmount(
    computedSummary?.current?.yearLossesBroughtForward,
  );

  return Math.abs(
    currentYABroughtForward ||
      normaliseAmount(computedSummary?.preceding?.currentYearLosses),
  );
};

const buildSingleYAUnutilisedLossRowsForCrossYA = ({
  amountKey,
  company,
  computedSummary,
  currencyRate,
  unutilisedLosses,
}) => {
  const singleYALosses = buildSingleYARows(unutilisedLosses, amountKey).filter(
    (row) => !isGeneratedCrossYACurrentYearLossRow(row),
  );

  if (amountKey !== "current_amount") return singleYALosses;

  const lossesBroughtForward =
    getCurrentYALossesBroughtForward(computedSummary);
  if (lossesBroughtForward <= 0) return singleYALosses;

  const precedingFinancialYear = Number(company?.yearOfAssessment) - 2;
  if (!Number.isFinite(precedingFinancialYear)) return singleYALosses;

  const isMatchingFinancialYearRow = (row) =>
    !row?.isForfeited &&
    row?.status !== "autoload" &&
    getFinancialYear(row) === precedingFinancialYear;

  const matchingRows = singleYALosses.filter(isMatchingFinancialYearRow);
  const mergedAmount = matchingRows.reduce(
    (sum, row) => sum + Math.abs(normaliseAmount(row?.amount)),
    lossesBroughtForward,
  );

  if (matchingRows.length === 0) {
    return [
      ...singleYALosses,
      {
        row_id: "auto-preceding-ya-current-year-loss",
        date: new Date(precedingFinancialYear, 0),
        amount: mergedAmount,
        current_amount: mergedAmount,
        preceding_amount: 0,
        historyAmount: [mergedAmount],
        currentHistoryAmount: [mergedAmount],
        precedingHistoryAmount: [0],
        reference: "Current Year Losses - Preceding YA",
        exchangeRate: currencyRate ?? company?.currencyRate,
        systemData: true,
        isForfeited: false,
        toDeclareAgreement: false,
        agreementStatus: { dateOfNOA: null, isAgreed: null },
      },
    ];
  }

  let hasMergedMatchingRow = false;
  return singleYALosses.reduce((nextRows, row) => {
    if (!isMatchingFinancialYearRow(row)) {
      nextRows.push(row);
      return nextRows;
    }

    if (!hasMergedMatchingRow) {
      nextRows.push({
        ...row,
        date: row.date || new Date(precedingFinancialYear, 0),
        amount: mergedAmount,
        current_amount: mergedAmount,
        historyAmount: [mergedAmount],
        currentHistoryAmount: [mergedAmount],
      });
      hasMergedMatchingRow = true;
    }

    return nextRows;
  }, []);
};

const buildSingleYAReferenceData = (referenceData = {}, amountKey) =>
  Object.keys(referenceData || {}).reduce((nextData, key) => {
    const value = referenceData[key];
    nextData[key] = Array.isArray(value)
      ? buildSingleYARows(value, amountKey)
      : value;
    return nextData;
  }, {});

const createMergedPdfDoc = (bytes) => ({
  setProperties: () => {},
  output: (type = "arraybuffer") => {
    const uint8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const arrayBuffer = uint8.buffer.slice(
      uint8.byteOffset,
      uint8.byteOffset + uint8.byteLength,
    );

    if (type === "blob") {
      return new Blob([arrayBuffer], { type: "application/pdf" });
    }

    if (type === "arraybuffer") return arrayBuffer;

    return arrayBuffer;
  },
  save: (filename = "Main Tax Computation.pdf") => {
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
});

const mergeJsPdfDocs = async (docs) => {
  const mergedPdf = await PDFDocument.create();

  for (const doc of docs) {
    const sourcePdf = await PDFDocument.load(doc.output("arraybuffer"));
    const copiedPages = await mergedPdf.copyPages(
      sourcePdf,
      sourcePdf.getPageIndices(),
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return createMergedPdfDoc(await mergedPdf.save());
};

const buildMainCompPdfOptions = (options = {}) => ({
  pdfDensity: options?.pdfDensity,
  pdfSpacingOptions: options?.pdfSpacingOptions,
});

const buildObjectGeneratorArgs = ({
  forView,
  company,
  currency,
  currencyRate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  unutilisedDonations,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  taxPreviouslyAssessed,
  computedSummary,
  unabsorbedSummary,
  comments,
  reviewNote,
  isExcel,
  options,
}) => ({
  forView,
  company,
  currency,
  currencyRate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  unutilisedDonations,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  taxPreviouslyAssessed,
  computedSummary,
  unabsorbedSummary,
  comments,
  reviewNote,
  isExcel,
  ...buildMainCompPdfOptions(options),
});

const generateSingleYAForCrossYA = ({
  amountKey,
  forView,
  company,
  currency,
  currencyRate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  unutilisedDonations,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  taxPreviouslyAssessed,
  computedSummary,
  crossYAApportionmentContext,
  comments,
  reviewNote,
  isExcel = false,
  pdfDensity,
  pdfSpacingOptions,
}) => {
  const yaLabel =
    amountKey === "preceding_amount" ? "Preceding YA" : "Current YA";
  const singleCompany = buildSingleYACompany(company, amountKey);
  const singleYAUnutilisedLosses = buildSingleYAUnutilisedLossRowsForCrossYA({
    amountKey,
    company,
    computedSummary,
    currencyRate,
    unutilisedLosses,
  });

  return generateIrasMainCompPdf({
    forView,
    company: singleCompany,
    currency,
    currencyRate,
    netProfitBeforeTax: apportionAmount(
      netProfitBeforeTax,
      crossYAApportionmentContext,
      amountKey,
    ),
    referenceData: buildSingleYAReferenceData(referenceData, amountKey),
    unutilisedCapitalAllowances: buildSingleYARows(
      unutilisedCapitalAllowances,
      amountKey,
    ),
    unutilisedLosses: singleYAUnutilisedLosses,
    unutilisedDonations: buildSingleYARows(
      unutilisedDonations,
      amountKey,
    ),
    corporateIncomeTaxRebate: getYAConfigValue(
      corporateIncomeTaxRebate,
      amountKey,
    ),
    currentYearRebate: getYAConfigValue(currentYearRebate, amountKey),
    isStartup: getYAConfigValue(isStartup, amountKey),
    taxPreviouslyAssessed: buildSingleYARows(
      taxPreviouslyAssessed,
      amountKey,
    ),
    computedSummary:
      computedSummary?.[
        amountKey === "preceding_amount" ? "preceding" : "current"
      ] ?? computedSummary,
    unabsorbedSummary: EMPTY_UNABSORBED_SUMMARY,
    comments,
    reviewNote,
    isExcel,
    pdfDensity,
    pdfSpacingOptions,
    scheduleName: isExcel
      ? `Main Tax Comp (${yaLabel})`
      : `Main Tax Computation (${yaLabel})`,
  });
};

const generateCrossYAAsSingleYAExcel = async ({
  commonArgs,
  company,
  forView,
}) => {
  const sharedExcel = BasePdfGenerator.getSharedExcel();
  const excel = sharedExcel || new BaseExcelGenerator();

  if (!sharedExcel) {
    BasePdfGenerator.setSharedExcel(excel, { autoDownload: false });
  }

  try {
    generateSingleYAForCrossYA({
      ...commonArgs,
      amountKey: "preceding_amount",
    });
    generateSingleYAForCrossYA({
      ...commonArgs,
      amountKey: "current_amount",
    });

    if (sharedExcel) return excel;

    return await excel.download(
      forView,
      company?.name,
      "Main Tax Computation",
    );
  } finally {
    if (!sharedExcel) BasePdfGenerator.clearSharedExcel();
  }
};

export const generateMainCompPdf = (
  forView,
  company,
  currency,
  currencyRate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  currentYearLosses,
  unutilisedDonations,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  taxPreviouslyAssessed,
  computedSummary,
  unabsorbedSummary = DEFAULT_UNABSORBED_SUMMARY,
  crossYAApportionmentContext,
  comments,
  reviewNote,
  isExcel = false,
  options = {},
) => {
  const isCrossYA = company?.taxPeriodType === "cross_ya";
  const isInvestmentCompany = company?.companyType === "investment";
  const resolvedCrossYAApportionmentContext =
    crossYAApportionmentContext?.totalDays
      ? crossYAApportionmentContext
      : getCrossYAApportionmentContext(company, isCrossYA);

  const objectGeneratorArgs = buildObjectGeneratorArgs({
    forView,
    company,
    currency,
    currencyRate,
    netProfitBeforeTax,
    referenceData,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    unutilisedDonations,
    corporateIncomeTaxRebate,
    currentYearRebate,
    isStartup,
    taxPreviouslyAssessed,
    computedSummary,
    unabsorbedSummary,
    comments,
    reviewNote,
    isExcel,
    options,
  });

  if (isInvestmentCompany && !isCrossYA) {
    return generateInvestCoMainCompPdf(objectGeneratorArgs);
  }

  if (!isCrossYA && !isInvestmentCompany) {
    return generateIrasMainCompPdf(objectGeneratorArgs);
  }

  const shouldGenerateCrossYAAsSingleYA =
    isCrossYA &&
    Boolean(resolvedCrossYAApportionmentContext?.totalDays) &&
    hasManualUnutilisedRows(
      unutilisedCapitalAllowances,
      unutilisedLosses,
      unutilisedDonations,
    );

  if (shouldGenerateCrossYAAsSingleYA) {
    const commonArgs = {
      forView,
      company,
      currency,
      currencyRate,
      netProfitBeforeTax,
      referenceData,
      unutilisedCapitalAllowances,
      unutilisedLosses,
      unutilisedDonations,
      corporateIncomeTaxRebate,
      currentYearRebate,
      isStartup,
      taxPreviouslyAssessed,
      computedSummary,
      crossYAApportionmentContext: resolvedCrossYAApportionmentContext,
      comments,
      reviewNote,
      isExcel,
      ...buildMainCompPdfOptions(options),
    };

    if (isExcel) {
      return generateCrossYAAsSingleYAExcel({
        commonArgs,
        company,
        forView,
      });
    }

    return mergeJsPdfDocs([
      generateSingleYAForCrossYA({
        ...commonArgs,
        amountKey: "preceding_amount",
      }),
      generateSingleYAForCrossYA({
        ...commonArgs,
        amountKey: "current_amount",
      }),
    ]);
  }

  if (isCrossYA) {
    return generateIrasCrossYAMainCompPdf({
      ...objectGeneratorArgs,
      crossYAApportionmentContext: resolvedCrossYAApportionmentContext,
    });
  }

  const generator = generateSingleYA;
  const args = [
    company,
    currency,
    currencyRate,
    netProfitBeforeTax,
    referenceData,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    currentYearLosses,
    unutilisedDonations,
    corporateIncomeTaxRebate,
    currentYearRebate,
    isStartup,
    taxPreviouslyAssessed,
    computedSummary,
    isCrossYA
      ? EMPTY_UNABSORBED_SUMMARY
      : (unabsorbedSummary ?? DEFAULT_UNABSORBED_SUMMARY),
  ];

  if (isCrossYA) args.push(resolvedCrossYAApportionmentContext);

  args.push(comments, reviewNote, isExcel);

  return generator(forView, ...args);
};

export default generateMainCompPdf;
